import { defineStore } from 'pinia';
import type { FileUploadItem } from '@base-shared/types/file-uploader';

/**
 * Shape of the `uploadedFiles` state — mirrors the legacy Vuex
 * `fileUploaderComponent.uploadedFiles` payload so that all consumers can
 * migrate one-at-a-time without changing their handler logic.
 *
 * `prevAction` is the last action that triggered a state update; consumers
 * gate their reactions on specific values (e.g. `'processNewFilesWithInstance'`).
 */
export interface FileUploaderUploadedFiles {
  prevAction: string | null;
  files: FileUploadItem[];
  updatedFile?: FileUploadItem;
  deletedFile?: FileUploadItem;
}

/**
 * Configuration object dispatched from any component to update the
 * file-uploader dropzone behaviour (target component, dropzone ID, etc.).
 */
export interface FileUploaderConfig {
  targetComponent?: string;
  targetDropzoneId?: string;
  targetPlayerId?: string | null;
  triggerClickDropzone?: boolean;
  setDropzoneInMediaListModal?: boolean;
  unsetDropzoneInMediaListModal?: boolean;
  disabledMediaUpload?: boolean;
  mediaListUploadIconClicked?: boolean;
  acceptedFileTypes?: string[];
  [key: string]: unknown;
}

/**
 * Pinia store replacing the legacy Vuex `fileUploaderComponent` module.
 *
 * Three reactive pieces of state:
 * - `uploadedFiles` – current in-flight upload queue with the last action that
 *   updated it; consumers watch `prevAction` to react to lifecycle events.
 * - `deletedFile` – the most-recently deleted upload entry; watched by
 *   `file-uploader.js` to trigger a localStorage re-sync.
 * - `uploaderConfig` – configuration pushed by any component that needs to
 *   change the active dropzone target (e.g. navigating to a schedule player).
 */
export const useFileUploaderStore = defineStore('fileUploaderComponent', {
  state: () => ({
    uploadedFiles: { prevAction: null, files: [] } as FileUploaderUploadedFiles,
    deletedFile: null as FileUploadItem | null,
    uploaderConfig: {} as FileUploaderConfig
  }),
  actions: {
    /**
     * Updates the upload-queue state and records which action triggered the
     * change. Replaces the `fileUploaderSetUploadedFiles` Vuex action.
     */
    setUploadedFiles (payload: FileUploaderUploadedFiles): void {
      this.uploadedFiles = payload;
    },

    /**
     * Records the most recently deleted upload-queue entry so that
     * `file-uploader.js` can re-sync its reactive state from localStorage.
     * Replaces the `fileUploaderDeletedFileInLocalStorage` Vuex action.
     */
    notifyFileDeleted (file: FileUploadItem): void {
      this.deletedFile = file;
    },

    /**
     * Pushes a new dropzone configuration from any component that needs to
     * change the active upload target.
     * Replaces the `fileUploaderUpdateUploaderConfigData` Vuex action.
     */
    setUploaderConfig (config: FileUploaderConfig): void {
      this.uploaderConfig = config;
    }
  }
});
